import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../lib/database.types';

/**
 * Eligibility discounts: a discount line on a program's price list that only some students get.
 *
 * Both work the same way. An admin adds the line to a program's pricing with a negative amount.
 * `programs.price` stays the REGULAR price (the `update_program_price()` rollup skips the line),
 * and `create_invoice_on_enrollment()` bills the line only when the enrolling student qualifies:
 *
 * - BHCS Student (`bhcs_student`): the student has an active after-school enrollment.
 * - Foster City Resident (`foster_city_resident`): a parent of the student has ZIP 94404 on file.
 *   Kaleido programs only; the database refuses it on any other program's pricing.
 *
 * A program carries at most one of the two (the line-item picker stops offering the other once
 * one is on the program), so every surface asks for "the program's eligibility discount".
 */
export type EligibilityDiscountSubtype = 'bhcs_student' | 'foster_city_resident';

export interface EligibilityDiscount {
  subtype: EligibilityDiscountSubtype;
  /** Someone who qualifies: the program card's price prefix, and the admin badge. */
  holderLabel: string;
  /** Chip beside the discount line. */
  onlyLabel: string;
  /** The discounted price, shown beside "Regular Price" while advertising. */
  priceLabel: string;
  /** Prefix for the discounted price next to a program's price in admin lists. */
  shortLabel: string;
  /** Under the total once the discount applies to the chosen student. */
  appliedLabel: string;
  /** Who qualifies, for the tooltip. */
  rule: string;
}

/** A Foster City ZIP. `parents.postal_code` is always exactly five digits, so this is equality. */
export const FOSTER_CITY_ZIP = '94404';

export function isFosterCityZip(postalCode: string | null | undefined): boolean {
  return postalCode === FOSTER_CITY_ZIP;
}

export const ELIGIBILITY_DISCOUNTS: Record<EligibilityDiscountSubtype, EligibilityDiscount> = {
  bhcs_student: {
    subtype: 'bhcs_student',
    holderLabel: 'BHCS Student',
    onlyLabel: 'BHCS Students Only',
    priceLabel: 'BHCS Student Price',
    shortLabel: 'BHCS',
    appliedLabel: 'BHCS Student discount applied.',
    rule: 'BHCS Students are those with an active enrollment in an after-school program.',
  },
  foster_city_resident: {
    subtype: 'foster_city_resident',
    holderLabel: 'Foster City Resident',
    onlyLabel: 'Foster City Residents Only',
    priceLabel: 'Foster City Resident Price',
    shortLabel: 'Foster City',
    appliedLabel: 'Foster City Resident discount applied.',
    rule: `Foster City Residents are families with a parent whose ZIP code on file is ${FOSTER_CITY_ZIP}.`,
  },
};

export function isEligibilityDiscountSubtype(
  subtype: string | null | undefined
): subtype is EligibilityDiscountSubtype {
  return subtype === 'bhcs_student' || subtype === 'foster_city_resident';
}

/** The eligibility discount a program carries, from its pricing rows' subtypes, or null. */
export function findEligibilityDiscount(
  subtypes: ReadonlyArray<string | null | undefined>
): EligibilityDiscount | null {
  const subtype = subtypes.find(isEligibilityDiscountSubtype);
  return subtype ? ELIGIBILITY_DISCOUNTS[subtype] : null;
}

/**
 * The discounted price beside a program's regular price (`programs.price`, which excludes the
 * discount), from its pricing rows. Null when the program has no eligibility discount.
 */
export function discountedPriceOf(
  regularPrice: number,
  rows: ReadonlyArray<{ amount: number; subtype?: string | null }>
): { discount: EligibilityDiscount; price: number } | null {
  const discount = findEligibilityDiscount(rows.map((row) => row.subtype));
  if (!discount) return null;
  const discountTotal = rows
    .filter((row) => isEligibilityDiscountSubtype(row.subtype))
    .reduce((sum, row) => sum + row.amount, 0);
  return { discount, price: regularPrice + discountTotal };
}

/**
 * Does this student qualify for the discount? The same rule `create_invoice_on_enrollment()`
 * applies, so a quote matches the invoice it produces.
 *
 * Foster City asks the `is_foster_city_resident` RPC because a parent cannot read a co-parent's
 * ZIP under RLS. Takes the client so each app passes its own (and its tests' mock).
 */
export async function fetchDiscountEligibility(
  client: SupabaseClient<Database>,
  subtype: EligibilityDiscountSubtype,
  studentId: string
): Promise<boolean> {
  if (subtype === 'foster_city_resident') {
    const { data, error } = await client.rpc('is_foster_city_resident', { p_student_id: studentId });
    if (error) throw error;
    return data === true;
  }
  const { count } = await client
    .from('enrollments')
    .select('id, programs!inner(type)', { count: 'exact', head: true })
    .eq('student_id', studentId)
    .eq('is_active', true)
    .eq('programs.type', 'after-school');
  return (count ?? 0) > 0;
}

/**
 * Decides how much of an eligibility discount to surface on a pricing panel.
 *
 * Two different jobs are in play:
 *
 * - Before a student is chosen the panel is *advertising* a program. Product's call is to
 *   always show the discount here, so every parent learns it exists and what it's worth,
 *   whether or not they currently qualify.
 * - Once a student is chosen the panel is *quoting* a specific enrollment, so it must show
 *   only what that student will actually be charged.
 *
 * Advertising always shows both prices side by side and never folds the discount into a
 * single total — that is what keeps it honest, since no one number is presented as the
 * amount that will be invoiced. A panel with room for only one total should opt out via
 * `showBeforeStudentSelected: false` rather than advertise a figure it can't stand behind.
 */
export interface DiscountVisibilityInput {
  /** Does this program carry an eligibility discount line item at all? */
  hasDiscount: boolean;
  /**
   * Before a student is chosen, should the discount be advertised? True for the parent-facing
   * program list, program page and enrollment modal; false where the panel quotes one total.
   */
  showBeforeStudentSelected: boolean;
  /** Has a specific student been chosen for this enrollment? */
  studentSelected: boolean;
  /** Does the chosen student qualify? `undefined` while unknown. */
  studentQualifies: boolean | undefined;
}

export interface DiscountVisibility {
  /** Render the green discount line item. */
  showDiscountLine: boolean;
  /** Render "Regular Price" and the discounted price side by side. */
  showDualPrices: boolean;
  /** The single total should be the discounted price. */
  applyDiscountToTotal: boolean;
}

export function resolveDiscountVisibility({
  hasDiscount,
  showBeforeStudentSelected,
  studentSelected,
  studentQualifies,
}: DiscountVisibilityInput): DiscountVisibility {
  if (!hasDiscount) {
    return { showDiscountLine: false, showDualPrices: false, applyDiscountToTotal: false };
  }

  // A student is chosen and we know the answer: quote exactly the price that applies.
  const studentKnown = studentSelected && studentQualifies !== undefined;
  if (studentKnown) {
    const qualifies = studentQualifies === true;
    return {
      showDiscountLine: qualifies,
      showDualPrices: false,
      applyDiscountToTotal: qualifies,
    };
  }

  // No student chosen yet — advertising, not quoting. Both prices are shown side by side,
  // so neither is presented as the price this parent will pay.
  return {
    showDiscountLine: showBeforeStudentSelected,
    showDualPrices: showBeforeStudentSelected,
    applyDiscountToTotal: false,
  };
}
